import { CombinedState, configureStore, DeepPartial, Reducer, ReducersMapObject } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import createFilter from 'redux-persist-transform-filter';

import { boatReducer } from '@/entities/boat';
import { historicalDataReducer, historicalTableReducer } from '@/entities/historical-data';
import { modalReducer } from '@/entities/modal';
import { toastReducer } from '@/entities/toast';
import { userReducer } from '@/entities/user';
import { virtualCockpitReducer } from '@/entities/virtual-cockpit';
import { aggregationServiceApi } from '@/shared/api/aggregation-service.api';
import { boatServiceApi } from '@/shared/api/boat-service.api';
import { boatV2ServiceApi } from '@/shared/api/boat-v2-service.api';
import { dashboardServiceApi } from '@/shared/api/dashboard-service.api';
import { hlabServiceApi } from '@/shared/api/hlab-service.api';
import { hvarServiceApi } from '@/shared/api/hvar-service.api';
import { maintenanceServiceApi } from '@/shared/api/maintenance-service.api';
import { organizationServiceApi } from '@/shared/api/organization-service.api';
import { userServiceApi } from '@/shared/api/user-service.api';

import { createAbortUnusedQueryMiddleware } from './abort-unused-query.middleware';
import { rtkQueryErrorLogger } from './error-logger';
import { createReducerManager } from './reducer-manager';
import { StoreSchema, StoreWithReducerManager } from './store.schema';

const saveSubsetFilter = createFilter('boat', ['selectedId']);

const persistConfig = {
	key: 'boat',
	storage,
	whitelist: ['boat'],
	transforms: [saveSubsetFilter],
};

const rtkQueryApis = [
	userServiceApi,
	organizationServiceApi,
	boatServiceApi,
	boatV2ServiceApi,
	maintenanceServiceApi,
	dashboardServiceApi,
	hvarServiceApi,
	aggregationServiceApi,
	hlabServiceApi,
];

export function createStore(
	preloadedState?: DeepPartial<StoreSchema>,
	lazyReducers?: DeepPartial<ReducersMapObject<StoreSchema>>,
	hasPersistor?: boolean,
): StoreWithReducerManager {
	const rootReducers: ReducersMapObject<StoreSchema> = {
		...lazyReducers,
		user: userReducer,
		toast: toastReducer,
		modal: modalReducer,
		boat: boatReducer,
		historicalData: historicalDataReducer,
		virtualCockpit: virtualCockpitReducer,
		historicalTable: historicalTableReducer,
		[userServiceApi.reducerPath]: userServiceApi.reducer,
		[organizationServiceApi.reducerPath]: organizationServiceApi.reducer,
		[boatServiceApi.reducerPath]: boatServiceApi.reducer,
		[boatV2ServiceApi.reducerPath]: boatV2ServiceApi.reducer,
		[maintenanceServiceApi.reducerPath]: maintenanceServiceApi.reducer,
		[dashboardServiceApi.reducerPath]: dashboardServiceApi.reducer,
		[hvarServiceApi.reducerPath]: hvarServiceApi.reducer,
		[aggregationServiceApi.reducerPath]: aggregationServiceApi.reducer,
		[hlabServiceApi.reducerPath]: hlabServiceApi.reducer,
	};

	const reducerManager = createReducerManager(rootReducers);

	const persistedReducer = persistReducer(persistConfig, reducerManager.reduce as Reducer<CombinedState<StoreSchema>>);

	const store = configureStore({
		reducer: hasPersistor ? persistedReducer : reducerManager.reduce,
		preloadedState,
		middleware: (getDefaultMiddleware) =>
			getDefaultMiddleware({ serializableCheck: false })
				.concat(rtkQueryErrorLogger)
				.concat(createAbortUnusedQueryMiddleware(rtkQueryApis))
				.concat(userServiceApi.middleware)
				.concat(organizationServiceApi.middleware)
				.concat(boatServiceApi.middleware)
				.concat(boatV2ServiceApi.middleware)
				.concat(maintenanceServiceApi.middleware)
				.concat(dashboardServiceApi.middleware)
				.concat(hvarServiceApi.middleware)
				.concat(aggregationServiceApi.middleware)
				.concat(hlabServiceApi.middleware),
		devTools: IS_DEV,
	});

	const persistor = persistStore(store);

	return { ...store, persistor, reducerManager };
}
